import { useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { MagnifyingGlass, ArrowsClockwise } from "@phosphor-icons/react";
import { Field, SectionCard } from "./ramsUtils";
import { RamsEmergencyCard } from "./RamsEmergencyCard";

/** 8. Emergency arrangements — site arrangements on the left, nearest A&E + route map on the right. */
export function RamsEmergencySection({ doc, patchSection, applyEmergency, canEdit }) {
  const em = doc.sections.emergency || {};
  const [addr, setAddr] = useState("");
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const set = (k, v) => patchSection("emergency", { ...em, [k]: v });
  const lookup = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/hs/rams/${doc.id}/emergency/lookup`, addr.trim() ? { address: addr.trim() } : {});
      applyEmergency(data); setTick((t) => t + 1); setAddr("");
      toast.success(`Nearest A&E: ${data.emergency.nearest_ae?.name}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Lookup failed"); } finally { setBusy(false); }
  };
  const choose = async (placeId) => {
    setBusy(true);
    try {
      const { data } = await API.post(`/hs/rams/${doc.id}/emergency/select`, { place_id: placeId });
      applyEmergency(data); setTick((t) => t + 1); toast.success(`Using ${data.emergency.nearest_ae?.name}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't switch hospital"); } finally { setBusy(false); }
  };
  return (
    <SectionCard n={8} title="Emergency arrangements" hint="What everyone on the job does if something goes wrong — and where the nearest A&E is.">
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="First aider on site" value={em.first_aider} onChange={(v) => set("first_aider", v)} placeholder="e.g. Jay Patel (FAW, exp. 03/27)" testid="rams-em-first-aider" />
            <Field label="First-aid kit location" value={em.first_aid_kit} onChange={(v) => set("first_aid_kit", v)} placeholder="e.g. Van + site office" testid="rams-em-kit" />
          </div>
          <Field label="Assembly point" value={em.assembly_point} onChange={(v) => set("assembly_point", v)} placeholder="e.g. Front car park by the main gate" testid="rams-em-assembly" />
          <Field label="Emergency contacts" rows={2} value={em.emergency_contacts} onChange={(v) => set("emergency_contacts", v)} placeholder="Office 020 …, supervisor 07…, client duty manager …" testid="rams-em-contacts" />
          <Field label="Fire & evacuation procedure" rows={4} value={em.fire_procedure} onChange={(v) => set("fire_procedure", v)} testid="rams-em-fire" />
          <Field label="Task-specific arrangements" rows={3} value={em.notes} onChange={(v) => set("notes", v)} placeholder="Rescue plan, isolation points, spill kit, gas emergency number…" testid="rams-em-notes" />
        </div>
        <div className="space-y-3">
          <RamsEmergencyCard ramsId={doc.id} emergency={em} mapTick={tick} />
          {canEdit && (
            <div className="rounded-lg border border-border p-3 space-y-2" data-testid="rams-ae-tools">
              <Label className="text-xs">Site address used for the lookup</Label>
              <div className="flex gap-2">
                <Input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder={doc.address || "Enter the site address or postcode"} className="h-9 text-sm" data-testid="rams-ae-address" />
                <Button size="sm" onClick={lookup} disabled={busy || (!addr.trim() && !doc.address && doc.latitude == null)} className="h-9 shrink-0" data-testid="rams-ae-lookup"><MagnifyingGlass size={14} className="mr-1" /> {em.nearest_ae ? "Re-check" : "Find nearest A&E"}</Button>
              </div>
              {em.looked_up_at && <p className="text-[11px] text-muted-foreground">Looked up {new Date(em.looked_up_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}{em.origin?.address ? ` from ${em.origin.address}` : ""}</p>}
              {em.alternatives?.length > 0 && (
                <div>
                  <p className="text-xs font-medium mt-1 mb-1">Other A&amp;E departments nearby</p>
                  <div className="space-y-1">{em.alternatives.map((a) => (
                    <div key={a.place_id} className="flex items-center gap-2 text-xs rounded-md border border-border px-2 py-1.5" data-testid={`rams-ae-alt-${a.place_id}`}>
                      <span className="flex-1 min-w-0"><span className="font-medium">{a.name}</span><span className="text-muted-foreground"> · {a.miles} mi · {a.address}</span></span>
                      <Button size="sm" variant="ghost" className="h-6 text-[11px]" disabled={busy} onClick={() => choose(a.place_id)} data-testid={`rams-ae-use-${a.place_id}`}><ArrowsClockwise size={11} className="mr-1" /> Use this</Button>
                    </div>))}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
}
